import { useState } from "react";

/**
 * Confirm-then-execute button. Single click reveals an inline
 * Confirm/Cancel pair; Confirm fires the destructive action; Cancel
 * returns to the initial state.
 *
 * No modal, no dismissable dialog — the inline pattern keeps
 * destructive actions discoverable without an extra layer of UI to
 * cancel out of. Used across /accounts (archive, delete-lot),
 * /watchlists (delete), /alerts (delete), /journal (delete).
 *
 * Pass `isPending` from the consuming TanStack mutation to disable
 * everything while the action is in flight; the Confirm button shows
 * `pendingLabel` instead of `confirmLabel` during that window.
 */

type Size = "sm" | "xs";

const SIZE_CLASSES: Record<Size, string> = {
  sm: "px-2 py-1 text-xs",
  xs: "px-1.5 py-0.5 text-xs",
};

export interface ConfirmButtonProps {
  /** Text on the initial trigger (e.g. "Delete", "Archive"). */
  label: string;
  /** Optional aria-label on the initial trigger when `label` is too generic. */
  ariaLabel?: string;
  /** Action to fire on Confirm click. */
  onConfirm: () => void;
  /** From the consuming mutation — disables every control while in-flight. */
  isPending?: boolean;
  /** Text on the Confirm button (default "Confirm"). */
  confirmLabel?: string;
  /** Text on the Confirm button while pending (default "…"). */
  pendingLabel?: string;
  /** Padding/text-size token. Default "sm". */
  size?: Size;
  /** Extra disable signal beyond `isPending`. */
  disabled?: boolean;
}

export function ConfirmButton({
  label,
  ariaLabel,
  onConfirm,
  isPending = false,
  confirmLabel = "Confirm",
  pendingLabel = "…",
  size = "sm",
  disabled = false,
}: ConfirmButtonProps) {
  const [confirming, setConfirming] = useState(false);
  const sizeCls = SIZE_CLASSES[size];

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        disabled={disabled || isPending}
        aria-label={ariaLabel}
        className={`rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] ${sizeCls} text-[var(--color-text)] hover:bg-[var(--color-elevated)] disabled:cursor-not-allowed disabled:opacity-50`}
      >
        {label}
      </button>
    );
  }

  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={() => onConfirm()}
        disabled={disabled || isPending}
        className={`rounded-md bg-[var(--color-danger)] ${sizeCls} font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50`}
      >
        {isPending ? pendingLabel : confirmLabel}
      </button>
      <button
        type="button"
        onClick={() => setConfirming(false)}
        disabled={isPending}
        className={`rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] ${sizeCls} text-[var(--color-text)] hover:bg-[var(--color-elevated)] disabled:cursor-not-allowed disabled:opacity-50`}
      >
        Cancel
      </button>
    </span>
  );
}
